import React, { useState, useRef } from 'react';
import { Upload, Download, Users, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { supabase } from '../integrations/supabase/client';
import * as XLSX from 'xlsx';

interface ImportResult {
  success: number;
  errors: Array<{
    row: number;
    error: string;
    data?: any;
  }>;
}

export const BulkUserImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const downloadTemplate = () => {
    // Criar planilha Excel
    const data = [
      ['nome_completo', 'email', 'telefone', 'cargo', 'gerente_email', 'creci'],
      ['João Silva', 'joao.silva@exemplo.com', '(11) 99999-9999', 'corretor', 'gerente@foxvendas.com', 'CRECI/SP 123456'],
      ['Maria Santos', 'maria.santos@exemplo.com', '(11) 88888-8888', 'gerente', '', 'CRECI/SP 654321'],
      ['Ana Costa', 'ana.costa@exemplo.com', '(11) 77777-7777', 'rh', '', ''],
      ['Carlos Oliveira', 'carlos.oliveira@exemplo.com', '(11) 66666-6666', 'diretor', '', 'CRECI/SP 789012']
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuários');

    // Configurar largura das colunas
    worksheet['!cols'] = [
      { width: 25 }, // nome_completo
      { width: 30 }, // email
      { width: 18 }, // telefone
      { width: 12 }, // cargo
      { width: 25 }, // gerente_email
      { width: 20 }  // creci
    ];

    XLSX.writeFile(workbook, 'modelo_usuarios.xlsx');
    
    toast({
      title: "Template baixado!",
      description: "Use este arquivo Excel como modelo para importar usuários em lote",
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && (
      selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      selectedFile.type === 'application/vnd.ms-excel' ||
      selectedFile.name.endsWith('.xlsx') ||
      selectedFile.name.endsWith('.xls')
    )) {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast({
        title: "Arquivo inválido",
        description: "Por favor, selecione um arquivo Excel válido (.xlsx ou .xls)",
        variant: "destructive",
      });
    }
  };

  const processImport = async () => {
    if (!file) return;

    setLoading(true);
    setResult(null);

    try {
      // Ler arquivo Excel
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // Converter para CSV para compatibilidade com a edge function
      const csvContent = XLSX.utils.sheet_to_csv(worksheet);
      
      const { data, error } = await supabase.functions.invoke('bulk-user-import', {
        body: { csvContent }
      });

      if (error) throw error;

      setResult(data);
      
      if (data.errors.length === 0) {
        toast({
          title: "Importação concluída!",
          description: `${data.success} usuários importados com sucesso`,
        });
      } else {
        toast({
          title: "Importação parcial",
          description: `${data.success} usuários importados, ${data.errors.length} erros encontrados`,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Erro na importação:', error);
      toast({
        title: "Erro na importação",
        description: error.message || "Erro interno do servidor",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Importação em Lote</h3>
          <p className="text-gray-600">Importe múltiplos usuários usando um arquivo Excel</p>
        </div>
        <Button
          onClick={downloadTemplate}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Baixar Modelo
        </Button>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-2">Instruções de uso:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Baixe o arquivo modelo clicando no botão "Baixar Modelo"</li>
              <li>• Preencha os dados dos usuários seguindo o formato do exemplo</li>
              <li>• Campos obrigatórios: nome_completo, email, cargo</li>
              <li>• Cargos válidos: corretor, gerente, rh, diretor</li>
              <li>• Para corretores, o campo "gerente_email" é obrigatório</li>
              <li>• Salve o arquivo Excel (.xlsx) e faça o upload</li>
            </ul>
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <div className="text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <label htmlFor="excel-file" className="cursor-pointer">
              <span className="mt-2 block text-sm font-medium text-gray-900">
                Clique para selecionar o arquivo Excel
              </span>
              <span className="mt-1 block text-sm text-gray-500">
                Ou arraste e solte aqui
              </span>
            </label>
            <input
              ref={fileInputRef}
              id="excel-file"
              type="file"
              className="hidden"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
            />
          </div>
        </div>
      </div>

      {/* Selected File */}
      {file && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-gray-600" />
              <div>
                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={processImport}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Users className="h-4 w-4" />
                {loading ? 'Processando...' : 'Importar Usuários'}
              </Button>
              <Button
                onClick={clearFile}
                variant="outline"
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Success Summary */}
          {result.success > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <h4 className="text-sm font-medium text-green-900">
                    ✅ {result.success} usuários importados com sucesso
                  </h4>
                  <p className="text-sm text-green-800">
                    Todos os usuários receberam emails de boas-vindas com suas credenciais
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Errors */}
          {result.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-900 mb-3">
                    ❌ {result.errors.length} erro(s) encontrado(s):
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {result.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-800 bg-red-100 p-2 rounded">
                        <strong>Linha {error.row}:</strong> {error.error}
                        {error.data && (
                          <div className="text-xs text-red-600 mt-1">
                            Dados: {JSON.stringify(error.data)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};